import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelectTheme } from "../../store";
import { TooltipIcon } from "../../asset/image";

const ICON_SIZE = 14;
const GAP = 10;
const VIEWPORT_MARGIN = 8;
const ARROW_INSET = 12;
const DEFAULT_WIDTH = "220px";
const Z_INDEX = 100000;

export interface TooltipProps {
  content?: string | React.ReactNode;
  tooltipWidth?: string;
  textAlign?: "start" | "end" | "center";
  alt?: string;
  className?: string;
  css?: { [key: string]: string | number };
}

interface TooltipCoords {
  top: number;
  left: number;
  placement: "top" | "bottom";
  arrowLeft: number;
}

const computeCoords = (iconEl: HTMLElement, tooltipEl: HTMLElement): TooltipCoords => {
  const iconRect = iconEl.getBoundingClientRect();
  const tipWidth = tooltipEl.offsetWidth;
  const tipHeight = tooltipEl.offsetHeight;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const iconCenterX = iconRect.left + iconRect.width / 2;

  const spaceAbove = iconRect.top - VIEWPORT_MARGIN;
  const spaceBelow = vh - iconRect.bottom - VIEWPORT_MARGIN;
  const need = tipHeight + GAP;
  const placement: "top" | "bottom" =
    spaceAbove >= need || spaceAbove >= spaceBelow ? "top" : "bottom";

  let top = placement === "top" ? iconRect.top - tipHeight - GAP : iconRect.bottom + GAP;
  top = Math.min(
    Math.max(top, VIEWPORT_MARGIN),
    Math.max(VIEWPORT_MARGIN, vh - VIEWPORT_MARGIN - tipHeight),
  );

  const maxLeft = Math.max(VIEWPORT_MARGIN, vw - VIEWPORT_MARGIN - tipWidth);
  const left = Math.min(Math.max(iconCenterX - tipWidth / 2, VIEWPORT_MARGIN), maxLeft);
  const arrowLeft = Math.min(
    Math.max(iconCenterX - left, ARROW_INSET),
    Math.max(ARROW_INSET, tipWidth - ARROW_INSET),
  );

  return { top, left, placement, arrowLeft };
};

export const Tooltip = (props: TooltipProps) => {
  const { color } = useSelectTheme();
  const { content, tooltipWidth, textAlign, alt, className, css: cssProp } = props;
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<TooltipCoords | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const reposition = useCallback(() => {
    const iconEl = containerRef.current;
    const tooltipEl = tooltipRef.current;
    if (!iconEl || !tooltipEl) return;
    const next = computeCoords(iconEl, tooltipEl);
    setCoords((prev) => {
      if (
        prev &&
        prev.top === next.top &&
        prev.left === next.left &&
        prev.placement === next.placement &&
        prev.arrowLeft === next.arrowLeft
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const setTooltipNode = useCallback(
    (node: HTMLDivElement | null) => {
      tooltipRef.current = node;
      if (node) reposition();
    },
    [reposition],
  );

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, content, tooltipWidth, reposition]);

  const tooltipBody = (
    <div
      ref={setTooltipNode}
      role="tooltip"
      css={{
        position: "fixed",
        top: coords?.top ?? 0,
        left: coords?.left ?? 0,
        minWidth: DEFAULT_WIDTH,
        width: tooltipWidth ?? DEFAULT_WIDTH,
        maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
        background: color.surface.dark,
        borderRadius: "5px",
        color: color.white,
        padding: "8px 12px",
        textAlign: textAlign ?? "center",
        whiteSpace: typeof content === "string" ? "pre-line" : undefined,
        zIndex: Z_INDEX,
        opacity: coords ? 1 : 0,
        pointerEvents: "none",
        boxShadow: "0px 4px 10px 1px #68686840",
        ":after": {
          content: '""',
          position: "absolute",
          left: coords?.arrowLeft ?? "50%",
          top: coords?.placement === "bottom" ? "auto" : "100%",
          bottom: coords?.placement === "bottom" ? "100%" : "auto",
          transform: "translateX(-50%)",
          borderLeft: "5px solid transparent",
          borderRight: "5px solid transparent",
          borderTop: coords?.placement === "bottom" ? "none" : `8px solid ${color.surface.dark}`,
          borderBottom: coords?.placement === "bottom" ? `8px solid ${color.surface.dark}` : "none",
        },
      }}
    >
      {content}
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={className}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      css={{
        position: "relative",
        display: "inline-flex",
        width: ICON_SIZE,
        minWidth: ICON_SIZE,
        height: ICON_SIZE,
        minHeight: ICON_SIZE,
        background: color.white,
        cursor: "pointer",
        ...cssProp,
      }}
    >
      <img width={ICON_SIZE} src={TooltipIcon} alt={alt ?? "Tooltip icon"} />
      {open && createPortal(tooltipBody, document.body)}
    </div>
  );
};
